import { useState, useEffect, useRef } from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Send, Mail, Calendar, Archive,
  MessageCircle, User, CheckCheck, AlertTriangle, Search, Lock, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StudentLayout } from "@/components/student/layout/StudentLayout";
import { toast } from "@/hooks/use-toast";
import { useThread, useMessages, useSendMessage, useMarkAsRead, useCloseThread } from "@/hooks/use-inquiries";
import { useCurrentUser } from "@/hooks/use-current-user";
import { OPEN_THREAD_STATUSES } from "@/services/inquiryService";
import { gradeLabel, modeLabel, timelineLabel } from "@/lib/goalOptions";
import { cn } from "@/lib/utils";

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  const today = new Date();
  const isToday = date.toDateString() === today.toDateString();
  if (isToday) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return date.toLocaleDateString([], { month: "short", day: "numeric" }) +
    " · " + date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

const STATUS_CONFIG: Record<string, { label: string; cls: string }> = {
  OPEN:     { label: "Open",     cls: "bg-emerald-100 text-emerald-700" },
  INACTIVE: { label: "Inactive", cls: "bg-amber-100 text-amber-700" },
  CLOSED:   { label: "Closed",   cls: "bg-slate-100 text-slate-500" },
  ARCHIVED: { label: "Archived", cls: "bg-slate-100 text-slate-500" },
};

export default function InquiryDetailPage() {
  usePageTitle('Inquiry | BookMyCoaching');
  const { id: threadId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [replyText, setReplyText] = useState("");
  const [confirmClose, setConfirmClose] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { user } = useCurrentUser();

  const { data: thread, isLoading: threadLoading } = useThread(threadId);
  const { data: messagesData, isLoading: messagesLoading } = useMessages(threadId);
  const sendMessage = useSendMessage(threadId ?? "");
  const markAsRead = useMarkAsRead(threadId ?? "");
  const closeThread = useCloseThread(threadId ?? "");

  useEffect(() => {
    if (threadId && thread) {
      markAsRead.mutate();
    }
  }, [threadId, thread?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messagesData]);

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    try {
      await sendMessage.mutateAsync(replyText.trim());
      setReplyText("");
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : "Failed to send";
      if (msg === "RATE_LIMITED") {
        toast({ title: "Please wait for a response before sending more messages", variant: "destructive" });
      } else if (msg === "THREAD_NOT_OPEN") {
        toast({ title: "This conversation is no longer open", variant: "destructive" });
      } else {
        toast({ title: "Error", description: msg, variant: "destructive" });
      }
    }
  };

  const handleCloseThread = async () => {
    try {
      await closeThread.mutateAsync();
      setConfirmClose(false);
      toast({ title: "Conversation closed", description: "This conversation has been closed." });
    } catch {
      toast({ title: "Error", description: "Failed to close conversation.", variant: "destructive" });
    }
  };

  const isLoading = threadLoading || messagesLoading;
  const messages = messagesData?.content ?? [];
  const isThreadOpen = thread ? OPEN_THREAD_STATUSES.has(thread.status) : false;
  const isArchived = thread?.status === "ARCHIVED";
  const isClosed = thread?.status === "CLOSED";
  const isInactive = thread?.status === "INACTIVE";

  const inactiveDays = thread?.lastMessageAt
    ? Math.floor((Date.now() - new Date(thread.lastMessageAt).getTime()) / 86400000)
    : 0;

  const contextParts = thread
    ? [thread.subject, gradeLabel(thread.gradeBand) || thread.gradeBand, thread.curriculum, thread.goalType, modeLabel(thread.modePreference), timelineLabel(thread.startTimeline)].filter(Boolean)
    : [];

  const goToTeacher = () => {
    const handle = thread?.teacherHandle;
    if (handle) navigate(`/t/${handle}`);
  };

  const teacherName = thread ? (thread.teacherDisplayName ?? thread.teacherName) : "";
  const statusCfg = thread ? (STATUS_CONFIG[thread.status] ?? { label: thread.status, cls: "bg-slate-100 text-slate-500" }) : null;

  return (
    <StudentLayout contentClassName="overflow-hidden bg-slate-50" contentStyle={{ height: "calc(100svh - 3.5rem)" }}>

      {/* Back navigation */}
      <div className="px-4 md:px-6 pt-4 pb-2 shrink-0">
        <button
          onClick={() => navigate("/my-inquiries")}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-400 hover:text-slate-700 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> My Inquiries
        </button>
      </div>

      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-3">
            <div className="w-12 h-12 rounded-xl bg-slate-100 animate-pulse mx-auto" />
            <p className="text-sm text-slate-400">Loading conversation...</p>
          </div>
        </div>
      ) : !thread ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
              <MessageCircle className="w-6 h-6 text-slate-500" />
            </div>
            <h2 className="text-base font-bold text-slate-800 mb-2">Conversation not found</h2>
            <Button size="sm" onClick={() => navigate("/my-inquiries")}>Back to Inquiries</Button>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0 max-w-3xl w-full mx-auto px-4 md:px-6 py-4">

          {/* ── Conversation header card ── */}
          <div className="mb-4 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden shrink-0">
            {/* Top row: avatar + name + status + close action */}
            <div className="flex items-center gap-3 px-4 py-3.5">
              <button
                type="button"
                onClick={goToTeacher}
                title={`View ${teacherName}'s profile`}
                className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 transition-colors flex items-center justify-center shrink-0"
              >
                <User className="w-5 h-5 text-slate-500" />
              </button>

              <div className="flex-1 min-w-0">
                <button
                  type="button"
                  onClick={goToTeacher}
                  className="text-sm font-bold text-slate-900 hover:text-indigo-600 transition-colors truncate block text-left"
                >
                  {teacherName}
                </button>
                {contextParts.length > 0 && (
                  <p className="text-xs text-slate-400 truncate mt-0.5">{contextParts.join(" · ")}</p>
                )}
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {statusCfg && (
                  <span className={cn("text-xs font-medium px-2.5 py-1 rounded-full", statusCfg.cls)}>
                    {statusCfg.label}
                  </span>
                )}
                {isThreadOpen && !confirmClose && (
                  <button
                    type="button"
                    onClick={() => setConfirmClose(true)}
                    title="Close conversation"
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Shared context row: email + time slots */}
            {(thread.sharedEmail || (thread.preferredTimeSlots && thread.preferredTimeSlots.length > 0)) && (
              <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-2">
                {thread.sharedEmail && (
                  <div className="flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
                    <span className="text-xs text-slate-600">{thread.sharedEmail}</span>
                  </div>
                )}
                {thread.preferredTimeSlots && thread.preferredTimeSlots.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Calendar className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
                    <div className="flex flex-wrap gap-1.5">
                      {thread.preferredTimeSlots.map((s, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center bg-indigo-100 text-indigo-700 text-xs font-medium px-2 py-0.5 rounded-md"
                        >
                          {s.date} · {s.timeSlot}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-1 text-xs text-slate-400 ml-auto">
                  <Lock className="w-3 h-3" />
                  <span>Shared with {teacherName.split(" ")[0]}</span>
                </div>
              </div>
            )}
          </div>

          {/* ── Confirm close ── */}
          {confirmClose && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center justify-between gap-3 shrink-0">
              <p className="text-xs text-red-700 font-medium">Close this conversation? This cannot be undone.</p>
              <div className="flex gap-2 shrink-0">
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setConfirmClose(false)}>Cancel</Button>
                <Button size="sm" className="h-7 text-xs bg-red-600 hover:bg-red-700 text-white" onClick={handleCloseThread} disabled={closeThread.isPending}>
                  {closeThread.isPending ? "Closing…" : "Close"}
                </Button>
              </div>
            </div>
          )}

          {/* ── Inactive nudge ── */}
          {isInactive && !isArchived && !isClosed && (
            <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 shrink-0">
              <div className="flex items-center gap-2 text-sm text-amber-800">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                {teacherName} hasn't replied in {inactiveDays} days.
              </div>
              <div className="flex flex-wrap gap-2 mt-2.5">
                <Button size="sm" variant="outline" onClick={() => navigate("/search")}>
                  <Search className="w-3.5 h-3.5 mr-1.5" /> Find similar teachers
                </Button>
              </div>
            </div>
          )}

          {/* ── Message list ── */}
          <div className="flex-1 overflow-y-auto space-y-4 pb-2 min-h-0">
            {messages.length === 0 && (
              <p className="text-center py-8 text-sm text-slate-400">No messages yet. Say hello!</p>
            )}
            {messages.map((msg) => {
              const isMine = msg.senderUserId === user?.id;
              return (
                <div
                  key={msg.id}
                  className={cn("flex gap-2.5", isMine ? "flex-row-reverse" : "flex-row")}
                >
                  {/* Avatar */}
                  <button
                    type="button"
                    onClick={isMine ? undefined : goToTeacher}
                    title={isMine ? undefined : `View ${teacherName}'s profile`}
                    className={cn(
                      "w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-xs font-bold self-end",
                      isMine
                        ? "bg-indigo-600 text-white"
                        : "bg-slate-100 text-slate-500 hover:bg-slate-200 cursor-pointer"
                    )}
                  >
                    {isMine
                      ? getInitials(user?.name || user?.email || "Me")
                      : <User className="w-4 h-4" />
                    }
                  </button>

                  {/* Bubble */}
                  <div className={cn("max-w-[72%] space-y-1", isMine ? "items-end" : "items-start")}>
                    <div className={cn(
                      "rounded-2xl px-4 py-2.5 text-sm leading-relaxed",
                      isMine
                        ? "bg-indigo-600 text-white rounded-br-sm"
                        : "bg-white border border-slate-200 rounded-bl-sm text-slate-800"
                    )}>
                      {msg.content}
                    </div>
                    <p className={cn(
                      "text-xs text-slate-400 px-1 flex items-center gap-1",
                      isMine ? "justify-end" : "justify-start"
                    )}>
                      {formatTime(msg.createdAt)}
                      {isMine && (
                        <span className="flex items-center gap-0.5">
                          · <CheckCheck className={cn("w-3 h-3", msg.seenAt ? "text-indigo-500" : "text-slate-300")} />
                          {msg.seenAt ? "Seen" : "Sent"}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* ── Reply area / closed state ── */}
          {isClosed ? (
            <div className="mt-4 py-3 px-4 bg-slate-50 rounded-xl border border-slate-200 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
              <X className="w-3.5 h-3.5" />
              This conversation was closed.
            </div>
          ) : isArchived ? (
            <div className="mt-4 py-3 px-4 bg-slate-50 rounded-xl border border-slate-200 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
              <Archive className="w-3.5 h-3.5" />
              This conversation is archived. You cannot send new messages.
            </div>
          ) : (
            <div className="mt-4 bg-white rounded-xl border border-slate-200 p-3 flex gap-2.5 items-end shrink-0">
              <Textarea
                placeholder="Write a message..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                rows={2}
                className="flex-1 resize-none border-0 bg-transparent shadow-none focus-visible:ring-0 p-0 text-sm placeholder:text-slate-300 min-h-[40px]"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendReply();
                  }
                }}
              />
              <Button
                onClick={handleSendReply}
                disabled={!replyText.trim() || sendMessage.isPending}
                size="sm"
                className="shrink-0 h-9 w-9 p-0 rounded-lg"
              >
                <Send className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>
      )}
    </StudentLayout>
  );
}
